import { createSelector, createSlice } from "@reduxjs/toolkit";
import { requests, requestStates } from "../../constants/requests";
import {
  isIncome,
  participantName,
  toPaymentPayload,
} from "../../utils/expense";
import {
  addToOutbox,
  applyPending,
  applyPendingNames,
  createOutboxEntry,
  findCreateEntry,
  hasHold,
  isHeld,
  isTempId,
  onlyHeldEntries,
  outboxKinds,
  releaseAllHolds,
  releaseHold,
  requestOf,
  retryDelay,
  undoEntry,
} from "../../utils/outbox";
import { announceOwnEntry } from "../../utils/own-entries";
import { randomId } from "../../utils/random-id";
import { isOnline } from "../../hooks/use-online";
import {
  ApiError,
  apiFetch,
  errorMessage,
  isCachedResult,
  isRetryable,
  isUnreachable,
  noop,
  reportTo,
  run,
} from "../api";

const initialState = {
  groups: [],
  session: {
    id: null,
    selectedGroup: null,
  },
  outbox: [],
  requestStates: {},
  groupLoadedAt: null,
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSelectedGroup: (state, action) => {
      state.session.selectedGroup = action.payload;
    },
    setRequestState: (state, action) => {
      state.requestStates = { ...state.requestStates, ...action.payload };
    },
    setSessionId: (state, action) => {
      state.session.id = action.payload;
    },
    setGroups: (state, action) => {
      state.groups = action.payload;
    },
    setGroupLoadedAt: (state, action) => {
      state.groupLoadedAt = action.payload;
    },
    outboxQueued: (state, action) => {
      state.outbox = addToOutbox(state.outbox ?? [], action.payload);
    },
    outboxEntryDone: (state, action) => {
      state.outbox = (state.outbox ?? []).filter(
        (entry) => entry.key !== action.payload,
      );
    },
    outboxEntryFailed: (state, action) => {
      const entry = state.outbox?.find((e) => e.key === action.payload.key);
      if (entry) {
        entry.attempts += 1;
        entry.retryAt = action.payload.retryAt;
      }
    },
    outboxCleared: (state) => {
      state.outbox = [];
    },
    outboxHoldReleased: (state, action) => {
      state.outbox = releaseHold(state.outbox ?? [], action.payload);
    },
    outboxHoldsReleased: (state) => {
      state.outbox = releaseAllHolds(state.outbox ?? []);
    },
    outboxEntryUndone: (state, action) => {
      state.outbox = undoEntry(
        state.outbox ?? [],
        action.payload.key,
        action.payload.restored,
      );
    },
  },
});

export const {
  setSelectedGroup,
  setGroups,
  setRequestState,
  setSessionId,
  setGroupLoadedAt,
  outboxQueued,
  outboxEntryDone,
  outboxEntryFailed,
  outboxCleared,
  outboxHoldReleased,
  outboxHoldsReleased,
  outboxEntryUndone,
} = appSlice.actions;

const NO_ENTRIES = [];

export const selectSessionId = (state) => state.session.id;
export const selectServerGroup = (state) => state.session.selectedGroup;
export const selectCurrentGroupId = (state) => state.session.selectedGroup?.id;
export const selectOutbox = (state) => state.outbox ?? NO_ENTRIES;
export const selectHeldCount = (state) =>
  selectOutbox(state).filter(hasHold).length;
export const selectCurrentGroup = createSelector(
  [selectServerGroup, selectOutbox],
  applyPending,
);
export const selectGroups = createSelector(
  [(state) => state.groups, selectOutbox],
  applyPendingNames,
);
export const selectGroupLoadedAt = (state) => state.groupLoadedAt;

const NO_TAGS = [];

export const selectGroupTags = createSelector(
  [selectCurrentGroup],
  (group) => (Array.isArray(group?.tags) ? group.tags : NO_TAGS),
);

export const selectExpenseTags = (state, expenseId) => {
  const expense = selectCurrentGroup(state)?.expenses?.find((e) =>
    sameId(e.id, expenseId),
  );
  return Array.isArray(expense?.tags) ? expense.tags : NO_TAGS;
};

export const selectRequestState = (state, requestName) =>
  state.requestStates[requestName] ?? requestStates.notSent;

export const selectIsPending = (state, requestName) =>
  selectRequestState(state, requestName) === requestStates.sent;

const track = (dispatch, requestName, state) =>
  dispatch(setRequestState({ [requestName]: state }));

const sameId = (a, b) => `${a}` === `${b}`;

const hasGroupId = (groupId) => groupId !== undefined && groupId !== null;

export const selectMe = createSelector([selectCurrentGroup], (group) =>
  hasGroupId(group?.me)
    ? (group.participants?.find((p) => sameId(p.id, group.me)) ?? null)
    : null,
);

// Responses can arrive out of order; never store one older than the last.
let lastTicket = 0;
let lastStoredTicket = 0;
let latestGroupStatusTicket = 0;

const takeTicket = () => ++lastTicket;

const storeSelectedGroup = (dispatch, ticket, group) => {
  if (ticket <= lastStoredTicket) return;
  lastStoredTicket = ticket;
  dispatch(setSelectedGroup(group));
  if (group && !isCachedResult(group)) {
    dispatch(setGroupLoadedAt(Date.now()));
  }
};

// Use instead of setSelectedGroup so that pending responses cannot overwrite it.
export const showGroup = (group) => (dispatch) =>
  storeSelectedGroup(dispatch, takeTicket(), group);

export const createGroup =
  (name, onSuccess = noop, onError = noop) =>
  (dispatch, getState) =>
    run(
      apiFetch(selectSessionId(getState()), "/groups", {
        method: "POST",
        body: { name },
      }),
      onSuccess,
      reportTo(onError, "Die Gruppe konnte nicht erstellt werden."),
    );

// After reloadFailed the participants are stored; a retry would add them twice.
export const addParticipantsErrors = {
  addFailed: "ADD_PARTICIPANTS_FAILED",
  reloadFailed: "GROUP_RELOAD_FAILED",
};

const postParticipants =
  (body, groupId, onSuccess, onError) => (dispatch, getState) => {
    if (!groupId) {
      return Promise.resolve();
    }
    const sessionId = selectSessionId(getState());
    const failure = (code, fallback) => (error) => {
      throw Object.assign(new Error(errorMessage(error, fallback)), { code });
    };

    track(dispatch, requests.addParticipants, requestStates.sent);
    return run(
      apiFetch(sessionId, `/groups/${groupId}/participants`, {
        method: "POST",
        body,
      }).then(
        (added) => {
          getGroups(dispatch, sessionId);
          return apiFetch(sessionId, `/groups/${groupId}`).then(
            (group) => ({ group, added }),
            failure(
              addParticipantsErrors.reloadFailed,
              "Das Laden der Gruppe hat nicht funktioniert.",
            ),
          );
        },
        failure(
          addParticipantsErrors.addFailed,
          "Das Hinzufügen der Teilnehmer hat nicht funktioniert.",
        ),
      ),
      ({ group, added }) => {
        track(dispatch, requests.addParticipants, requestStates.success);
        onSuccess(group, added);
      },
      (error) => {
        track(dispatch, requests.addParticipants, requestStates.error);
        onError(error.message, {
          code: error.code,
          participantsSaved: error.code === addParticipantsErrors.reloadFailed,
        });
      },
    );
  };

export const addParticipants = (
  participants,
  groupId,
  onSuccess = noop,
  onError = noop,
) => postParticipants({ participants }, groupId, onSuccess, onError);

export const addMe =
  (name, groupId, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    const ticket = takeTicket();
    return dispatch(
      postParticipants(
        { participants: [name], assign: true },
        groupId,
        (group, added) => {
          if (sameId(selectCurrentGroupId(getState()), groupId)) {
            storeSelectedGroup(dispatch, ticket, group);
          }
          onSuccess(group, added);
        },
        onError,
      ),
    );
  };

export const renameParticipant =
  (groupId, participantId, name, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    if (!hasGroupId(groupId)) {
      return Promise.resolve();
    }
    const ticket = takeTicket();
    track(dispatch, requests.renameParticipant, requestStates.sent);
    return run(
      apiFetch(
        selectSessionId(getState()),
        `/groups/${groupId}/participants/${participantId}`,
        { method: "PUT", body: { name } },
      ),
      (group) => {
        if (sameId(selectCurrentGroupId(getState()), groupId)) {
          storeSelectedGroup(dispatch, ticket, group);
        }
        track(dispatch, requests.renameParticipant, requestStates.success);
        onSuccess(group);
      },
      (error) => {
        track(dispatch, requests.renameParticipant, requestStates.error);
        onError(
          errorMessage(error, "Die Person konnte nicht umbenannt werden."),
        );
      },
    );
  };

const getGroups = (dispatch, sessionId, onSuccess = noop, onError = noop) => {
  track(dispatch, requests.loadGroups, requestStates.sent);
  return run(
    apiFetch(sessionId, "/groups"),
    (groups) => {
      dispatch(setGroups(groups));
      track(dispatch, requests.loadGroups, requestStates.success);
      onSuccess(groups);
    },
    (error) => {
      track(dispatch, requests.loadGroups, requestStates.error);
      onError(errorMessage(error, "Die Gruppen konnten nicht geladen werden."));
    },
  );
};

export const refreshGroups =
  (onSuccess = noop, onError = noop) =>
  (dispatch, getState) =>
    getGroups(dispatch, selectSessionId(getState()), onSuccess, onError);

export const getGroupStatus =
  (groupId, silent = false, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    if (!hasGroupId(groupId)) {
      return Promise.resolve();
    }
    const ticket = takeTicket();
    latestGroupStatusTicket = ticket;
    const isLatest = () => ticket === latestGroupStatusTicket;

    track(dispatch, requests.loadGroupStatus, requestStates.sent);
    return run(
      apiFetch(selectSessionId(getState()), `/groups/${groupId}`),
      (group) => {
        if (isLatest()) {
          storeSelectedGroup(dispatch, ticket, group);
          track(dispatch, requests.loadGroupStatus, requestStates.success);
        }
        onSuccess(group);
      },
      (error) => {
        if (isLatest()) {
          track(dispatch, requests.loadGroupStatus, requestStates.error);
        }
        if (!silent) {
          onError(
            errorMessage(error, "Die Gruppe konnte nicht geladen werden."),
          );
        }
      },
    );
  };

export const joinGroup =
  (hash, onSuccess = noop, onError = noop) =>
  (dispatch, getState) =>
    run(
      apiFetch(selectSessionId(getState()), `/join/${hash}`),
      onSuccess,
      reportTo(onError, "Der Beitritt zur Gruppe hat nicht funktioniert."),
    );

export const assignMe =
  (groupId, participantId, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    if (!hasGroupId(groupId)) {
      return Promise.resolve();
    }
    const ticket = takeTicket();
    track(dispatch, requests.assignMe, requestStates.sent);
    return run(
      apiFetch(selectSessionId(getState()), `/groups/${groupId}/me`, {
        method: "PUT",
        body: { participant_id: participantId },
      }),
      (group) => {
        if (sameId(selectCurrentGroupId(getState()), groupId)) {
          storeSelectedGroup(dispatch, ticket, group);
        }
        track(dispatch, requests.assignMe, requestStates.success);
        onSuccess(group);
      },
      (error) => {
        track(dispatch, requests.assignMe, requestStates.error);
        onError(
          errorMessage(
            error,
            participantId === null
              ? "Die Zuordnung konnte nicht aufgehoben werden."
              : "Die Zuordnung konnte nicht gespeichert werden.",
          ),
        );
      },
    );
  };

export const removeGroup =
  (groupId, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    const sessionId = selectSessionId(getState());
    const ticket = takeTicket();
    track(dispatch, requests.removeGroup, requestStates.sent);
    return run(
      apiFetch(sessionId, `/groups/${groupId}`, { method: "DELETE" }),
      () => {
        if (sameId(selectCurrentGroupId(getState()), groupId)) {
          storeSelectedGroup(dispatch, ticket, null);
        }
        track(dispatch, requests.removeGroup, requestStates.success);
        onSuccess();
        return getGroups(dispatch, sessionId);
      },
      (error) => {
        track(dispatch, requests.removeGroup, requestStates.error);
        onError(
          errorMessage(error, "Die Gruppe konnte nicht verlassen werden."),
        );
      },
    );
  };

const IDEMPOTENCY_KEY_LENGTH = 32;

export const SENDING_TEXT =
  "Der Eintrag wird gerade gesendet. Bitte öffne ihn gleich noch einmal.";

const flushingKeys = new Set();

const isWaitingForConnection = (outbox) =>
  !isOnline() || !onlyHeldEntries(outbox);

const UNREACHABLE = true;

export const undoQueuedEntry = (key, restored) => (dispatch, getState) => {
  if (flushingKeys.has(key)) return false;
  const outbox = selectOutbox(getState());
  const present = outbox.some((entry) => entry.key === key);
  const restorable =
    restored?.entry && !outbox.some((e) => e.key === restored.entry.key);
  if (!present && !restorable) return false;
  dispatch(outboxEntryUndone({ key, restored }));
  return true;
};

const undoOf = (entry, outbox) => {
  const create = isTempId(entry.targetId)
    ? findCreateEntry(outbox, entry.targetId)
    : undefined;
  return {
    undo: undoQueuedEntry(
      entry.key,
      create ? { entry: create, index: outbox.indexOf(create) } : undefined,
    ),
    release: outboxHoldReleased(entry.key),
  };
};

const queue = (
  dispatch,
  getState,
  entry,
  requestName,
  onSuccess,
  unreachable = false,
) => {
  const outbox = selectOutbox(getState());
  const queued =
    !(entry.kind === outboxKinds.deleteExpense && isTempId(entry.targetId)) &&
    (unreachable || isWaitingForConnection(outbox));
  const undoable = hasHold(entry) ? undoOf(entry, outbox) : {};
  dispatch(outboxQueued(entry));
  track(dispatch, requestName, requestStates.success);
  const group = selectCurrentGroup(getState());
  return run(
    Promise.resolve(
      sameId(group?.id, entry.groupId) ? group : { id: entry.groupId },
    ),
    (current) => onSuccess(current, { queued, ...undoable }),
    noop,
  );
};

const sendOrQueue =
  (
    {
      requestName,
      failedText,
      rejectedText,
      groupId: fixedGroupId,
      holdMs,
      ...change
    },
    onSuccess,
    onError,
  ) =>
  (dispatch, getState) => {
    const groupId = fixedGroupId ?? selectCurrentGroupId(getState());
    if (!hasGroupId(groupId)) {
      onError(`${failedText} Es ist keine Gruppe ausgewählt.`);
      return Promise.resolve();
    }
    const entry = createOutboxEntry({
      ...change,
      key: randomId(IDEMPOTENCY_KEY_LENGTH),
      groupId,
      holdUntil: holdMs > 0 ? Date.now() + holdMs : undefined,
      rejectedText:
        typeof rejectedText === "function"
          ? rejectedText(getState())
          : rejectedText,
    });
    const outbox = selectOutbox(getState());

    if (isTempId(entry.targetId)) {
      const create = findCreateEntry(outbox, entry.targetId);
      if (!create || flushingKeys.has(create.key)) {
        onError(SENDING_TEXT);
        return Promise.resolve();
      }
      return queue(dispatch, getState, entry, requestName, onSuccess);
    }
    if (!isOnline() || outbox.length > 0 || hasHold(entry)) {
      return queue(dispatch, getState, entry, requestName, onSuccess);
    }

    const ticket = takeTicket();
    const { method, path, body } = requestOf(entry);
    track(dispatch, requestName, requestStates.sent);
    return run(
      apiFetch(selectSessionId(getState()), path, {
        method,
        body,
        idempotencyKey: entry.key,
      }),
      (group) => {
        if (sameId(selectCurrentGroupId(getState()), groupId)) {
          storeSelectedGroup(dispatch, ticket, group);
        }
        track(dispatch, requestName, requestStates.success);
        onSuccess(group);
      },
      (error) => {
        if (isUnreachable(error)) {
          return queue(
            dispatch,
            getState,
            entry,
            requestName,
            onSuccess,
            UNREACHABLE,
          );
        }
        track(dispatch, requestName, requestStates.error);
        onError(errorMessage(error, failedText));
      },
    );
  };

export const rejectionMessage = (entry, error) => {
  const reason = error instanceof ApiError ? error.body?.message : undefined;
  return reason ? `${entry.rejectedText} ${reason}` : entry.rejectedText;
};

let currentFlush = null;
let flushRequestedMeanwhile = null;

const flushEntries = async (dispatch, getState, ignoreBackoff, onRejected) => {
  let renamed = false;
  let rejectedSelected = false;
  try {
    for (;;) {
      const [entry] = selectOutbox(getState());
      if (!entry || !isOnline()) return {};
      if (isHeld(entry)) return { retryAt: entry.holdUntil };
      if (!ignoreBackoff && entry.retryAt > Date.now()) {
        return { retryAt: entry.retryAt };
      }
      const ticket = takeTicket();
      const { method, path, body } = requestOf(entry);
      flushingKeys.add(entry.key);
      try {
        const group = await apiFetch(selectSessionId(getState()), path, {
          method,
          body,
          idempotencyKey: entry.key,
          keepalive: body === undefined,
        });
        dispatch(outboxEntryDone(entry.key));
        if (sameId(selectCurrentGroupId(getState()), entry.groupId)) {
          storeSelectedGroup(dispatch, ticket, group);
        }
        renamed ||= entry.kind === outboxKinds.renameGroup;
      } catch (error) {
        if (isRetryable(error)) {
          const retryAt = Date.now() + retryDelay(entry.attempts + 1);
          dispatch(outboxEntryFailed({ key: entry.key, retryAt }));
          return { retryAt };
        }
        dispatch(outboxEntryDone(entry.key));
        onRejected(rejectionMessage(entry, error));
        rejectedSelected ||= sameId(
          selectCurrentGroupId(getState()),
          entry.groupId,
        );
      } finally {
        flushingKeys.delete(entry.key);
      }
    }
  } finally {
    if (renamed) getGroups(dispatch, selectSessionId(getState()));
    if (rejectedSelected) {
      dispatch(getGroupStatus(selectCurrentGroupId(getState()), true));
    }
  }
};

const flushUntilSettled = async (dispatch, getState, options) => {
  try {
    let next = options;
    let result = {};
    while (next) {
      flushRequestedMeanwhile = null;
      try {
        result = await flushEntries(
          dispatch,
          getState,
          next.ignoreBackoff,
          next.onRejected,
        );
      } catch (error) {
        console.error(error);
        result = {};
      }
      next = flushRequestedMeanwhile;
    }
    return result;
  } finally {
    currentFlush = null;
    flushRequestedMeanwhile = null;
  }
};

// A release or entry arriving mid-flush must not wait for the next trigger.
export const flushOutbox =
  ({ ignoreBackoff = false, onRejected = noop } = {}) =>
  (dispatch, getState) => {
    if (currentFlush) {
      const ignore = !!flushRequestedMeanwhile?.ignoreBackoff;
      flushRequestedMeanwhile = {
        ignoreBackoff: ignoreBackoff || ignore,
        onRejected,
      };
      return currentFlush;
    }
    currentFlush = flushUntilSettled(dispatch, getState, {
      ignoreBackoff,
      onRejected,
    });
    return currentFlush;
  };

export const updateGroup = (groupId, name, onSuccess = noop, onError = noop) =>
  sendOrQueue(
    {
      requestName: requests.renameGroup,
      kind: outboxKinds.renameGroup,
      groupId,
      body: { name },
      failedText: "Die Gruppe konnte nicht geändert werden.",
      rejectedText: `Die Gruppe konnte nicht in „${name.trim()}“ umbenannt werden.`,
    },
    onSuccess,
    onError,
  );

const entryOf = (state, id) =>
  selectCurrentGroup(state)?.expenses?.find((e) => sameId(e.id, id));

const expenseText = (title, action, entry = null) => {
  const noun = isIncome(entry) ? "Einnahme" : "Ausgabe";
  return title
    ? `Die ${noun} „${title}“ konnte nicht ${action} werden.`
    : `Die ${noun} konnte nicht ${action} werden.`;
};

const deleteEntry = (
  expenseId,
  failedText,
  rejectedText,
  onSuccess,
  onError,
  { holdMs } = {},
) =>
  sendOrQueue(
    {
      requestName: requests.deleteExpense,
      kind: outboxKinds.deleteExpense,
      targetId: expenseId,
      failedText,
      rejectedText,
      holdMs,
    },
    onSuccess,
    onError,
  );

export const deleteExpense = (
  expenseId,
  onSuccess = noop,
  onError = noop,
  options = {},
) =>
  deleteEntry(
    expenseId,
    "Die Ausgabe konnte nicht gelöscht werden.",
    (state) => {
      const entry = entryOf(state, expenseId);
      return expenseText(entry?.name, "gelöscht", entry);
    },
    onSuccess,
    onError,
    options,
  );

export const deletePayment = (
  expenseId,
  onSuccess = noop,
  onError = noop,
  options = {},
) =>
  deleteEntry(
    expenseId,
    "Die Zahlung konnte nicht gelöscht werden.",
    "Die Zahlung konnte nicht gelöscht werden.",
    onSuccess,
    onError,
    options,
  );

export const deleteParticipant = (
  participantId,
  onSuccess = noop,
  onError = noop,
) =>
  sendOrQueue(
    {
      requestName: requests.deleteParticipant,
      kind: outboxKinds.deleteParticipant,
      targetId: participantId,
      failedText: "Die Person konnte nicht entfernt werden.",
      rejectedText: (state) => {
        const name = participantName(
          selectCurrentGroup(state)?.participants,
          participantId,
          "",
        );
        return name
          ? `${name} konnte nicht entfernt werden.`
          : "Die Person konnte nicht entfernt werden.";
      },
    },
    onSuccess,
    onError,
  );

// A queued entry may be sent days later; its time is the moment of saving.
const withSpentAt = (expense, isUpdate) =>
  isUpdate || expense?.spent_at != null
    ? expense
    : { ...expense, spent_at: new Date().toISOString() };

const saveEntry = (
  { requestName, expenseId, expense, failedText, rejectedText },
  onSuccess,
  onError,
) =>
  sendOrQueue(
    {
      requestName,
      kind: hasGroupId(expenseId)
        ? outboxKinds.updateExpense
        : outboxKinds.createExpense,
      targetId: expenseId ?? null,
      body: { expense: withSpentAt(expense, hasGroupId(expenseId)) },
      failedText,
      rejectedText,
    },
    (...result) => {
      if (!hasGroupId(expenseId)) announceOwnEntry(result[0]?.id);
      onSuccess(...result);
    },
    onError,
  );

const saveExpense = (expenseId, expense, onSuccess, onError) =>
  saveEntry(
    {
      requestName: requests.saveExpense,
      expenseId,
      expense,
      failedText: expenseText("", "gespeichert", expense),
      rejectedText: expenseText(expense?.title?.trim(), "gespeichert", expense),
    },
    onSuccess,
    onError,
  );

export const createExpense = (expense, onSuccess = noop, onError = noop) =>
  saveExpense(undefined, expense, onSuccess, onError);

export const updateExpense = (
  expenseId,
  expense,
  onSuccess = noop,
  onError = noop,
) => saveExpense(expenseId, expense, onSuccess, onError);

const savePayment = (expenseId, payment, onSuccess, onError) =>
  saveEntry(
    {
      requestName: requests.savePayment,
      expenseId,
      expense: toPaymentPayload(payment),
      failedText: "Die Zahlung konnte nicht gespeichert werden.",
      rejectedText: "Die Zahlung konnte nicht gespeichert werden.",
    },
    onSuccess,
    onError,
  );

export const createPayment = (payment, onSuccess = noop, onError = noop) =>
  savePayment(undefined, payment, onSuccess, onError);

export const updatePayment = (
  expenseId,
  payment,
  onSuccess = noop,
  onError = noop,
) => savePayment(expenseId, payment, onSuccess, onError);

const tagRequest =
  (requestName, method, body, failedText) =>
  (groupId, tagId, onSuccess = noop, onError = noop) =>
  (dispatch, getState) => {
    if (!hasGroupId(groupId)) {
      return Promise.resolve();
    }
    const ticket = takeTicket();
    track(dispatch, requestName, requestStates.sent);
    return run(
      apiFetch(selectSessionId(getState()), `/groups/${groupId}/tags/${tagId}`, {
        method,
        body,
      }),
      (group) => {
        if (sameId(selectCurrentGroupId(getState()), groupId)) {
          storeSelectedGroup(dispatch, ticket, group);
        }
        track(dispatch, requestName, requestStates.success);
        onSuccess(group);
      },
      (error) => {
        track(dispatch, requestName, requestStates.error);
        onError(errorMessage(error, failedText));
      },
    );
  };

export const updateTag = (
  groupId,
  tagId,
  changes,
  onSuccess = noop,
  onError = noop,
) =>
  tagRequest(
    requests.saveTag,
    "PUT",
    changes,
    "Der Tag konnte nicht gespeichert werden.",
  )(groupId, tagId, onSuccess, onError);

export const deleteTag = (groupId, tagId, onSuccess = noop, onError = noop) =>
  tagRequest(
    requests.deleteTag,
    "DELETE",
    undefined,
    "Der Tag konnte nicht gelöscht werden.",
  )(groupId, tagId, onSuccess, onError);

const SESSION_ID_LENGTH = 16;

export const createSessionId = () => (dispatch, getState) => {
  if (!selectSessionId(getState())) {
    dispatch(setSessionId(randomId(SESSION_ID_LENGTH)));
  }
};

export default appSlice.reducer;
